import { ARABIC_LOCALE } from '@/constants/i18n';

export const locales = ['en', 'fr', 'es', ARABIC_LOCALE] as const;

export type Locale = (typeof locales)[number];

export const defaultLocale: Locale = 'en';

/** Locales rendered under `/[locale]` (English stays at `/`). */
export const localizedLocales = ['fr', 'es', 'ar'] as const satisfies readonly Locale[];

export type LocalizedLocale = (typeof localizedLocales)[number];

export const localeDisplayNames: Record<Locale, string> = {
  en: 'English',
  fr: 'Français',
  es: 'Español',
  ar: 'العربية',
};

export function isLocale(value: string): value is Locale {
  return (locales as readonly string[]).includes(value);
}

export function dirForLocale(locale: Locale): 'ltr' | 'rtl' {
  return locale === ARABIC_LOCALE ? 'rtl' : 'ltr';
}

export function localePath(locale: Locale): string {
  return locale === defaultLocale ? '/' : `/${locale}`;
}

/**
 * Prefix a site path with the locale segment (`en` stays unprefixed).
 * `path` should be locale-agnostic (e.g. `/cookie-settings` or `/`).
 */
export function localizedHref(locale: Locale, path: string): string {
  const normalized =
    path === '' || path === '/'
      ? '/'
      : path.startsWith('/')
        ? path
        : `/${path}`;

  if (normalized === '/') {
    return localePath(locale);
  }

  return locale === defaultLocale ? normalized : `/${locale}${normalized}`;
}

/** Strip a leading `/fr|/es|/ar` segment so language switches keep the same page. */
export function pathWithoutLocale(pathname: string): string {
  for (const locale of localizedLocales) {
    if (pathname === `/${locale}`) {
      return '/';
    }
    const prefix = `/${locale}/`;
    if (pathname.startsWith(prefix)) {
      const rest = pathname.slice(prefix.length - 1);
      return rest === '' ? '/' : rest;
    }
  }

  return pathname === '' ? '/' : pathname;
}

export function localeHreflang(locale: Locale): string {
  return locale;
}
